if (!customElements.get('ib-media-detail')) {
  customElements.define('ib-media-detail', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.gallery = this.closest('ib-product-gallery');
      this.dialog = this.querySelector('dialog');
      this.viewport = this.querySelector('.ib-media-detail-viewport');
      this.image = this.querySelector('[data-detail-image]');
      this.status = this.querySelector('[data-detail-status]');
      this.pointers = new Map();

      this.querySelector('[data-detail-close]').addEventListener('click', () => this.requestClose(), {signal});
      this.querySelector('[data-detail-previous]').addEventListener('click', () => this.move(-1), {signal});
      this.querySelector('[data-detail-next]').addEventListener('click', () => this.move(1), {signal});
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.requestClose();
      }, {signal});
      this.dialog.addEventListener('close', () => this.afterClose(), {signal});
      this.dialog.addEventListener('keydown', (event) => this.onKeydown(event), {signal});
      this.viewport.addEventListener('wheel', (event) => this.onWheel(event), {signal, passive: false});
      this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event), {signal});
      this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event), {signal});
      this.viewport.addEventListener('pointerup', (event) => this.onPointerUp(event), {signal});
      this.viewport.addEventListener('pointercancel', (event) => this.onPointerCancel(event), {signal});
      this.image.addEventListener('load', () => this.onImageLoad(), {signal});
      window.addEventListener('resize', () => {
        if (this.dialog.open) this.fit();
      }, {signal});
      window.addEventListener('popstate', () => {
        if (this.dialog.open) {
          this.closingFromHistory = true;
          this.dialog.close();
        }
      }, {signal});

      this.bindGalleryFigures();
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.abort?.abort();
      this.abort = null;
    }

    bindGalleryFigures() {
      this.figures = [...this.gallery.querySelectorAll('.ib-pdp-gallery__image')];
      this.figures.forEach((figure, index) => {
        let press;
        figure.addEventListener('pointerdown', (event) => {
          press = {x: event.clientX, y: event.clientY};
        }, {signal: this.abort.signal});
        figure.addEventListener('pointerup', (event) => {
          if (press && Math.hypot(event.clientX - press.x, event.clientY - press.y) < 8) {
            this.open(index, figure);
          }
          press = null;
        }, {signal: this.abort.signal});
        figure.addEventListener('pointercancel', () => { press = null; }, {signal: this.abort.signal});
        figure.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.open(index, figure);
          }
        }, {signal: this.abort.signal});
      });
    }

    open(index, opener) {
      this.figures = [...(this.gallery.images || this.gallery.querySelectorAll('.ib-pdp-gallery__image'))].filter(figure => !figure.hidden);
      if (opener) index = this.figures.indexOf(opener);
      if (!this.figures.length) return;
      this.index = Math.max(0, Math.min(this.figures.length - 1, index ?? this.gallery.currentIndex ?? 0));
      this.opener = opener || this.figures[this.index];
      this.previousOverflow = document.documentElement.style.overflow;
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      history.pushState({...history.state, ibMediaViewer: true}, '', location.href);
      this.historyEntry = true;
      this.loadImage();
      this.querySelector('[data-detail-close]').focus({preventScroll: true});
    }

    requestClose() {
      if (!this.dialog.open) return;
      if (this.historyEntry && history.state?.ibMediaViewer) {
        history.back();
      } else {
        this.dialog.close();
      }
    }

    afterClose() {
      document.documentElement.style.overflow = this.previousOverflow || '';
      this.pointers.clear();
      this.gesture = null;
      this.dragStart = null;
      this.opener?.focus({preventScroll: true});
      if (!this.closingFromHistory && this.historyEntry && history.state?.ibMediaViewer) history.back();
      this.closingFromHistory = false;
      this.historyEntry = false;
    }

    loadImage() {
      const figure = this.figures[this.index];
      const source = figure.querySelector('img');
      this.zoom = 1;
      this.x = 0;
      this.y = 0;
      this.maxZoom = 1;
      this.pointers.clear();
      this.gesture = null;
      this.dragStart = null;
      this.image.style.opacity = '0';
      this.image.alt = source.alt;
      this.image.src = figure.dataset.detailSrc || source.currentSrc || source.src;
      this.status.textContent = `${this.index + 1} / ${this.figures.length}`;
      this.style.setProperty('--ib-detail-progress', String((this.index + 1) / this.figures.length));
      this.querySelector('[data-detail-previous]').disabled = this.index === 0;
      this.querySelector('[data-detail-next]').disabled = this.index === this.figures.length - 1;
      if (this.image.complete && this.image.naturalWidth) this.onImageLoad();
    }

    onImageLoad() {
      this.fit();
      this.image.style.opacity = '1';
    }

    fit() {
      if (!this.image.naturalWidth || !this.viewport.clientWidth || !this.viewport.clientHeight) return;
      const mobile = matchMedia('(max-width: 749px)').matches;
      const inset = mobile ? 0 : 24;
      const availableWidth = Math.max(1, this.viewport.clientWidth - inset * 2);
      const availableHeight = Math.max(1, this.viewport.clientHeight - inset * 2);
      const ratio = Math.min(availableWidth / this.image.naturalWidth, availableHeight / this.image.naturalHeight);
      this.baseWidth = this.image.naturalWidth * ratio;
      this.baseHeight = this.image.naturalHeight * ratio;
      this.maxZoom = Math.max(1, Math.min(3, this.image.naturalWidth / this.baseWidth, this.image.naturalHeight / this.baseHeight));
      this.zoom = Math.min(this.zoom || 1, this.maxZoom);
      this.image.style.width = `${this.baseWidth}px`;
      this.image.style.height = `${this.baseHeight}px`;
      this.render();
    }

    render() {
      const overflowX = Math.max(0, (this.baseWidth * this.zoom - this.viewport.clientWidth) / 2);
      const overflowY = Math.max(0, (this.baseHeight * this.zoom - this.viewport.clientHeight) / 2);
      this.x = Math.max(-overflowX, Math.min(overflowX, this.x));
      this.y = Math.max(-overflowY, Math.min(overflowY, this.y));
      this.image.style.transform = `translate(calc(-50% + ${this.x}px), calc(-50% + ${this.y}px)) scale(${this.zoom})`;
      this.viewport.classList.toggle('is-zoomed', this.zoom > 1.01);
    }

    setZoom(value, clientX, clientY) {
      const next = Math.max(1, Math.min(this.maxZoom, value));
      const rect = this.viewport.getBoundingClientRect();
      const pointX = clientX == null ? 0 : clientX - rect.left - rect.width / 2;
      const pointY = clientY == null ? 0 : clientY - rect.top - rect.height / 2;
      if (this.zoom) {
        this.x = pointX - (pointX - this.x) * next / this.zoom;
        this.y = pointY - (pointY - this.y) * next / this.zoom;
      }
      this.zoom = next;
      if (next === 1) {
        this.x = 0;
        this.y = 0;
      }
      this.render();
    }

    move(delta) {
      const next = Math.max(0, Math.min(this.figures.length - 1, this.index + delta));
      if (next === this.index) return;
      this.index = next;
      this.gallery.setActive(next, {scroll: true});
      this.loadImage();
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.move(1);
      } else if (this.zoom > 1.01 && ['ArrowUp', 'ArrowDown'].includes(event.key)) {
        event.preventDefault();
        this.x += event.key === 'ArrowLeft' ? 60 : event.key === 'ArrowRight' ? -60 : 0;
        this.y += event.key === 'ArrowUp' ? 60 : event.key === 'ArrowDown' ? -60 : 0;
        this.render();
      }
    }

    onWheel(event) {
      event.preventDefault();
      this.setZoom(this.zoom * Math.exp(-event.deltaY * 0.002), event.clientX, event.clientY);
    }

    onPointerDown(event) {
      if (event.button !== 0) return;
      this.viewport.setPointerCapture(event.pointerId);
      this.pointers.set(event.pointerId, {x: event.clientX, y: event.clientY});
      if (this.pointers.size === 1) {
        this.dragStart = {x: event.clientX, y: event.clientY, time: Date.now(), moved: false};
      } else if (this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        this.gesture = {distance: Math.hypot(a.x - b.x, a.y - b.y), zoom: this.zoom};
        this.dragStart = null;
      }
    }

    onPointerMove(event) {
      const previous = this.pointers.get(event.pointerId);
      if (!previous) return;
      this.pointers.set(event.pointerId, {x: event.clientX, y: event.clientY});
      if (this.pointers.size === 2 && this.gesture) {
        const [a, b] = [...this.pointers.values()];
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        this.setZoom(this.gesture.zoom * distance / Math.max(1, this.gesture.distance), (a.x + b.x) / 2, (a.y + b.y) / 2);
        return;
      }
      if (this.dragStart && Math.hypot(event.clientX - this.dragStart.x, event.clientY - this.dragStart.y) > 8) this.dragStart.moved = true;
      if (this.zoom > 1.01) {
        this.x += event.clientX - previous.x;
        this.y += event.clientY - previous.y;
        this.viewport.classList.add('is-dragging');
        this.render();
      }
    }

    onPointerUp(event) {
      this.pointers.delete(event.pointerId);
      this.viewport.classList.remove('is-dragging');
      if (this.gesture) {
        if (this.pointers.size < 2) this.gesture = null;
        this.dragStart = null;
        return;
      }
      const start = this.dragStart;
      this.dragStart = null;
      if (!start) return;
      const deltaX = event.clientX - start.x;
      const deltaY = event.clientY - start.y;
      if (this.zoom <= 1.01 && Math.abs(deltaX) > 52 && Math.abs(deltaX) > Math.abs(deltaY) * 1.35) {
        this.move(deltaX < 0 ? 1 : -1);
        return;
      }
      if (!start.moved && Date.now() - start.time < 450) {
        const doubleTap = event.pointerType === 'mouse' || Date.now() - (this.lastTap || 0) < 300;
        if (doubleTap) this.setZoom(this.zoom > 1.01 ? 1 : Math.min(2.5, this.maxZoom), event.clientX, event.clientY);
        this.lastTap = Date.now();
      }
    }

    onPointerCancel(event) {
      this.pointers.delete(event.pointerId);
      this.gesture = null;
      this.dragStart = null;
      this.viewport.classList.remove('is-dragging');
    }
  });
}
